import { HttpContextToken } from '@angular/common/http';
import { AppSettingsService } from '@core/services/app-settings.service';

/**
 * App-settings hosts that are considered "core / must-succeed". A 401 from any
 * other host (extensions like Shopify, analytics, the localhost desktop/native
 * bridges, third-parties or unknown hosts) must NOT trigger the global
 * /unauthorized redirect — those failures are feature-scoped and already handled
 * by their own callers.
 */
const CORE_API_KEYS = ['edaraApi', 'edaraCoreApi'] as const;

/**
 * When set to `true` on a request's HttpContext, the ErrorInterceptor will skip
 * the global /unauthorized redirect even for a core 401 — letting that specific
 * caller handle the 401 itself. Defaults to `false`, so existing requests are
 * fully backward compatible.
 *
 * Usage:
 *   this.http.get(url, { context: new HttpContext().set(SKIP_AUTH_REDIRECT, true) });
 */
export const SKIP_AUTH_REDIRECT = new HttpContextToken<boolean>(() => false);

/**
 * Extract a lowercased host (hostname[:port]) from a possibly relative or
 * malformed URL. Relative URLs are resolved against the current origin.
 * Returns undefined when the URL cannot be parsed.
 */
function safeHost(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url, window.location.origin).host.toLowerCase();
  } catch {
    return undefined;
  }
}

/**
 * True only when `requestUrl` targets one of the known CORE API hosts taken from
 * the runtime app-settings. Extension, analytics, localhost, third-party,
 * unknown and unparseable URLs all return false. Comparing hosts (not path
 * prefixes) makes this robust to trailing slashes and query strings, and reading
 * from AppSettingsService keeps it correct across dev/staging/prod.
 */
export function isCoreApiRequest(requestUrl: string | undefined): boolean {
  const requestHost = safeHost(requestUrl);
  if (!requestHost) {
    return false;
  }

  const settings = AppSettingsService.appSettings;
  if (!settings) {
    // Settings not loaded yet -> treat as non-core (non-blocking).
    return false;
  }

  return CORE_API_KEYS.some(key => {
    const coreHost = safeHost(settings[key]?.baseUrl);
    return coreHost !== undefined && coreHost === requestHost;
  });
}
